<script setup>
import { fetchJson, getArtById, getLogged, avatarUrl } from '@/services/api.js'
import { trackEvent } from '@/services/analytics.js'
import CommentItem from '@/components/comment.vue'
import CommentsItem from '@/components/comments.vue'
import ItemPerfil from '@/components/modals/perfil.vue'
import Sk from '@/components/usefulness/Sk.vue'
import ClientOnly from '@/components/ClientOnly.vue'
import { ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from '@/stores/index.js'
import { usePageDataStore } from '@/stores/pageData.js'

const store = useStore()
const pageData = usePageDataStore()
const route = useRoute()
const router = useRouter()

const id = ref(route.params.id)
const title = ref('')
const description = ref('')
const time = ref('')
const autor = ref('')
const imagem = ref('')
const user = ref('')
const perfilName = ref('')
const showP = ref(false)
const colors = ref([])
const loading = ref(true)

function applyArt(data) {
  title.value = data.title
  description.value = data.description
  time.value = data.time
  autor.value = data.autor
  imagem.value = data.imagem
  loading.value = false
}

async function getArt(artId) {
  loading.value = true
  const cached = pageData.art
  if (cached && String(cached.id) === String(artId)) {
    applyArt(cached)
    if (!import.meta.env.SSR) {
      await getCor({ value: cached.imagem })
    }
    return
  }

  const data = await getArtById(artId)

  if (!data[0].error) {
    applyArt(data[0])
    await getCor({ value: data[0].imagem })
    trackEvent('view_art', { item_id: String(artId) })
  } else if (import.meta.env.SSR) {
    loading.value = false
  } else {
    alert(data[0].text)
    await router.push({ path: '/error' })
  }
}

async function logado() {
  const data = await getLogged()
  user.value = data.nick ?? ''
}

async function getCor(img) {
  if (import.meta.env.SSR) return
  try {
    colors.value = await fetchJson(
      'https://api.pauloferraco.com.br/v3/colors.php?img=' + img.value + '&num=27',
      { credentials: 'omit' },
    ).catch(() => [])
  } catch (_) {
    colors.value = []
  }
}

if (pageData.art && String(pageData.art.id) === String(id.value)) {
  applyArt(pageData.art)
}

logado()
if (loading.value) {
  getArt(id.value)
}

watch(
  () => route.params.id,
  (newId) => {
    id.value = newId
    getArt(newId)
  },
)
</script>

<template>
  <div class="page art">
    <div class="top">
      <div class="icon"></div>
      <div class="title">
        <span v-if="!loading">{{ title }}</span>
        <Sk v-else w="160px" h="16px" extra="my-1" />
      </div>
    </div>
    <template v-if="loading">
      <div class="content"><Sk w="100%" h="320px" rounded="rounded-xl" /></div>
      <div class="item flex flex-col gap-2">
        <Sk w="100%" h="13px" />
        <Sk w="85%" h="13px" />
        <Sk w="92%" h="13px" />
      </div>
    </template>
    <template v-else>
    <div class="content">
      <img alt="arte" :src="imagem">
    </div>
    <div class="item" v-html="description"></div>
    <div v-if="colors.length" class="item">
      <div class="cor" v-for="(c, index) in colors" :key="index" :style="{backgroundColor: '#'+c}">#{{ c }}</div>
    </div>
    <div class="bottomInfo">
      <div>
        <div class="avatar"
             :style="{backgroundImage: `url('${avatarUrl(autor, { headonly: 1 })}')`}"></div>
        <div class="autor" @click="() => {showP = true; perfilName = autor}">Arte por <strong>{{ autor }}</strong></div>
      </div>
      <div class="time">{{ time }}</div>
    </div>
    </template>
    <ClientOnly>
      <CommentItem v-if="store.logged == 'true' || store.logged" type="art" :id="id" :user="user" />
      <CommentsItem type="art" :id="id" />
      <Transition name="slide-fade">
        <ItemPerfil v-if="showP" @close="showP = false" :user="perfilName" />
      </Transition>
    </ClientOnly>
  </div>
</template>

<style scoped>
.art .top .icon {
  background-color: #21415C;
  background-position: -32px -28px;
}

.art .content img {
  float: left;
  position: relative;
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 100%;
  left: 50%;
  transform: translateX(-50%);
  border-radius: 6px;
}

.art .item {
  float: left;
  width: 100%;
  height: auto;
  background-color: #E6EBF1;
  border-radius: 12px;
  padding: 16px;
  font-size: 14px;
  font-weight: 500;
  margin-top: 20px;
}

.item .cor {
  float: left;
  width: 74px;
  height: 26px;
  border-radius: 10px;
  margin: 4px 4px 0 4px;
  padding: 0 4px;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  line-height: 26px;
  color: #eee;
}

.bottomInfo {
  position: relative;
  display: flex;
  justify-content: space-between;
  width: 100%;
  height: 40px;
  background-color: #CA70A3;
  top: 10px;
  border-radius: 12px;
  color: #fff;
}

.bottomInfo .avatar {
  margin-left: 5px;
  height: 100%;
  width: 40px;
  float: left;
  background-position: center -36px;
  background-repeat: no-repeat;
}

.bottomInfo .autor,
.bottomInfo .time {
  float: left;
  width: auto;
  height: 20px;
  font-size: 14px;
  margin: 12px 10px 0 6px;
}
</style>
